import classNames from 'classnames';
import { useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import IconLabel from '@/components/molecules/icon-label/icon-label';
import ImageSlider from '@/components/organisms/image-slider/image-slider';
import TagList from '@/components/organisms/tag-list/tag-list';
import ExtraCardModal from '../extra-card-modal/extra-card-modal';
import styles from './extra-card.module.scss';

export interface ExtraCardProps {
  /** The aspect ratio to use for the image */
  aspectRatio?: Content.Properties.AspectRatio;
  /** The button text */
  buttonText: Content.Components['ExtraItemDto']['buttonText'];
  /** The class name to apply */
  className?: string;
  /** The description of the card */
  description: Content.Components['ExtraItemDto']['description'];
  /** The excluded features */
  excludedFeatures: Content.Components['ExtraItemDto']['excludedFeatures'];
  /** The excluded feature heading */
  excludedText: Content.Components['ExtraItemDto']['excludedText'];
  /** Tags to display on the card */
  filters: Content.Components['ExtraItemDto']['filters'];
  /** The headline of the card */
  headline: Content.Components['ExtraItemDto']['headline'];
  /** List of features to show in the card */
  highlightedFeatures: Content.Components['ExtraItemDto']['highlightedFeatures'];
  /** The images to show in the card */
  images: Content.Components['ExtraItemDto']['images'];
  /** The included features */
  includedFeatures: Content.Components['ExtraItemDto']['includedFeatures'];
  /** The included feature heading */
  includedText: Content.Components['ExtraItemDto']['includedText'];
  /** The link in the modal */
  link: Content.Components['ExtraItemDto']['link'];
  /** The description displayed under the included/excluded items */
  overlayDescription: Content.Components['ExtraItemDto']['overlayDescription'];
  /** The headline displayed under the included/excluded items */
  overlayHeadline: Content.Components['ExtraItemDto']['overlayHeadline'];
  /** The price items list */
  priceItems: Content.Components['ExtraItemDto']['priceItems'];
}

const ExtraCard = ({
  aspectRatio = 'fourByThree',
  buttonText,
  className,
  description,
  filters = [],
  excludedFeatures = [],
  excludedText,
  headline,
  highlightedFeatures = [],
  images = [],
  includedFeatures = [],
  includedText,
  link,
  overlayDescription,
  overlayHeadline,
  priceItems = [],
}: ExtraCardProps) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className={classNames(styles.component, className)}>
      <div
        className={classNames(styles.imageWrapper, {
          [styles.isFourByThree]: aspectRatio === 'fourByThree',
          [styles.isSixteenByNine]: aspectRatio === 'sixteenByNine',
        })}
      >
        <ImageSlider images={images.map(imageBlock => imageBlock.image)} />

        {filters.length > 0 && (
          <TagList
            className={styles.imageTagList}
            tags={filters.map(tag => ({
              text: tag.filter as string,
              variant: 'primary',
            }))}
          />
        )}
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.topSection}>
          <Typography
            className={styles.headline}
            tag="h3"
            tagStyle="headingMedium"
          >
            {headline}
          </Typography>

          {description && (
            <RichText className={styles.description} text={description} />
          )}
          {highlightedFeatures && highlightedFeatures.length > 0 && (
            <div className={styles.highlightedFeatures}>
              {highlightedFeatures.slice(0, 3).map((feature, featureIndex) => (
                <div key={featureIndex}>
                  <Typography
                    className={styles.headline}
                    tag="label"
                    tagStyle="bodyMediumBold"
                  >
                    {feature.headline}
                  </Typography>

                  <IconLabel
                    icon={feature.icon as IconName}
                    text={feature.text || ''}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.bottomSection}>
          <Button
            onClick={() => setShowModal(true)}
            text={buttonText}
            variant="primary"
          />
        </div>
      </div>

      <ExtraCardModal
        description={description}
        excludedFeatures={excludedFeatures}
        excludedText={excludedText}
        headline={headline}
        highlightedFeatures={highlightedFeatures}
        images={images}
        includedFeatures={includedFeatures}
        includedText={includedText}
        isOpen={showModal}
        link={link}
        onClose={() => setShowModal(false)}
        overlayDescription={overlayDescription}
        overlayHeadline={overlayHeadline}
        priceItems={priceItems}
      />
    </div>
  );
};

ExtraCard.displayName = 'ExtraCard';

export default ExtraCard;
